import React, { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Inbox, Search, Tag as TagIcon, UserRound, UserRoundX } from "lucide-react";
import { WsDropdown, WsSegmented } from "src/stage/WsControls";
import ConversationListItem from "./component/ConversationListItems";
import useTags from "src/features/Tags/hooks/useTags";
import { isRealtimeEnabled } from "src/realtime/echo";

// AUTO-WA-39: the Chats list in the Stage design. Filters answer the questions
// a team asks all day: what's unread, what's mine, what nobody has picked up.
const FILTERS = [
  { id: "all", label: "All", icon: Inbox },
  { id: "unread", label: "Unread" },
  { id: "mine", label: "Mine", icon: UserRound },
  { id: "unassigned", label: "Unassigned", icon: UserRoundX },
];

const EMPTY = {
  all: ["No chats yet", "When customers message your WhatsApp number, their chats appear here."],
  unread: ["You’re all caught up", "Every message has been read."],
  mine: ["Nothing assigned to you", "Chats you pick up or are given appear here."],
  unassigned: ["Every chat has an owner", "New chats nobody has picked up appear here."],
};

function ConversationList({
  conversationRecords = [],
  activeConversation,
  onConversationSelect,
  phoneNumber,
  query = { search: "", filter: "all", tag_id: "", page: 1 },
  onQueryChange = () => {},
  pagination,
  loading = false,
  realtimeConnected,
}) {
  const [searchQuery, setSearchQuery] = useState(query.search);
  const { tags: tagCatalog } = useTags();
  useEffect(() => {
    if (searchQuery === query.search) return undefined;
    const timer = setTimeout(() => onQueryChange({ search: searchQuery.trim() }), 300);
    return () => clearTimeout(timer);
  }, [searchQuery, query.search, onQueryChange]);

  const tagOptions = useMemo(() => [{ value: "", label: "All tags", short: "All" }, ...tagCatalog.map((tag) => ({ value: String(tag.id), label: tag.name }))], [tagCatalog]);
  const filter = FILTERS.some((item) => item.id === query.filter) ? query.filter : "all";
  const [emptyTitle, emptyText] = searchQuery ? ["No chats found", "Try a different name or phone number."] : EMPTY[filter];
  const activeId = activeConversation?.id ?? activeConversation?.conversation_id;
  const numberLabel = phoneNumber?.display_phone_number || phoneNumber?.phone_number || phoneNumber?.number;
  // Amber only while live updates are set up but dropped; without them the list still refreshes.
  const reconnecting = isRealtimeEnabled() && realtimeConnected === false;

  return (
    <aside className="wa-inbox-list ws-overlay" aria-label="Chats">
      <div className="wa-inbox-list-head">
        <div className="ws-grow" style={{ minWidth: 0 }}>
          <h1 className="wa-inbox-title">Chats</h1>
          <p className="wa-inbox-sub">
            <i className={reconnecting ? "is-off" : ""} aria-hidden="true" />
            {numberLabel || "Your WhatsApp number"}
            {reconnecting && <span className="wa-inbox-sub-note"> · Reconnecting…</span>}
          </p>
        </div>
        {tagCatalog.length > 0 && (
          <WsDropdown label="Tag" icon={TagIcon} value={String(query.tag_id || "")} options={tagOptions} onChange={(tag_id) => onQueryChange({ tag_id })} align="right" />
        )}
      </div>

      <div className="wa-inbox-tools">
        <label className="ws-searchbox wa-inbox-search">
          <Search size={15} aria-hidden="true" />
          <input value={searchQuery} onChange={(event) => setSearchQuery(event.target.value)} placeholder="Search name or number" aria-label="Search chats" />
        </label>
        <WsSegmented label="Show chats" items={FILTERS} value={filter} onChange={(value) => onQueryChange({ filter: value })} />
      </div>

      <div className="wa-inbox-items" aria-busy={loading}>
        {conversationRecords.length > 0 ? conversationRecords.map((conversation) => {
          const id = conversation.id ?? conversation.conversation_id;
          return (
            <ConversationListItem
              key={id}
              conversation={conversation}
              isActive={id != null && activeId != null && String(id) === String(activeId)}
              onSelect={onConversationSelect}
            />
          );
        }) : (
          <div className="wa-inbox-empty">
            <span className="ws-icon-tile" aria-hidden="true">{searchQuery ? <Search size={18} /> : <Inbox size={18} />}</span>
            <strong>{emptyTitle}</strong>
            <span>{emptyText}</span>
          </div>
        )}
      </div>

      {pagination && pagination.last_page > 1 && (
        <nav aria-label="Chat pages" className="wa-inbox-pager">
          <button type="button" className="ws-row-more" aria-label="Newer chats" disabled={loading || query.page <= 1} onClick={() => onQueryChange({ page: query.page - 1 })}><ChevronLeft size={16} /></button>
          <span role="status">{loading ? "Loading…" : pagination.from ? `${pagination.from}–${pagination.to} of ${pagination.total}` : `Page ${query.page} of ${pagination.last_page}`}</span>
          <button type="button" className="ws-row-more" aria-label="Older chats" disabled={loading || query.page >= pagination.last_page} onClick={() => onQueryChange({ page: query.page + 1 })}><ChevronRight size={16} /></button>
        </nav>
      )}
    </aside>
  );
}

export default ConversationList;
